/**
 * MigrationDlqTriagePanel — Spec §15.1 / §16.14 / §17.3.
 *
 * Per-job DLQ triage:
 *   - filter by error_class / error_code / suppressed
 *   - per-row actions: Replay, Suppress
 *   - bulk action: replay-bulk (with the current filter applied server-side)
 *   - expand a row to see error_message + diagnostic_context (and a download
 *     link to the raw blob, when raw_blob_storage_path is populated)
 *
 * Backend wiring:
 *   GET    /api/edgemail/admin/migration/jobs/:jobId/dlq           (list)
 *   POST   /api/edgemail/admin/migration/jobs/:jobId/dlq/:dlqId/replay
 *   POST   /api/edgemail/admin/migration/jobs/:jobId/dlq/replay-bulk
 *   POST   /api/edgemail/admin/migration/jobs/:jobId/dlq/:dlqId/suppress
 */
import React, { useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  StopIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import {
  useMigrationDlq,
  useReplayMigrationDlqEntry,
  useReplayMigrationDlqBulk,
  useSuppressMigrationDlqEntry,
} from '../../hooks/useMigrationJobs';
import type { MigrationDlqEntry } from '../../types/migration.types';

interface DlqFilters {
  error_code?: string;
  error_class?: string;
  suppressed?: boolean;
}

interface MigrationDlqTriagePanelProps {
  jobId: string | null;
}

export function MigrationDlqTriagePanel({ jobId }: MigrationDlqTriagePanelProps) {
  const [filters, setFilters] = useState<DlqFilters>({ suppressed: false });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useMigrationDlq(jobId, {
    error_code: filters.error_code,
    error_class: filters.error_class,
    suppressed: filters.suppressed,
    limit: 100,
  });

  const replayMutation = useReplayMigrationDlqEntry(jobId ?? '');
  const suppressMutation = useSuppressMigrationDlqEntry(jobId ?? '');
  const bulkMutation = useReplayMigrationDlqBulk(jobId ?? '');

  const distinctErrorCodes = useMemo(() => {
    const seen = new Set<string>();
    (data?.entries ?? []).forEach((e) => seen.add(e.error_code));
    return Array.from(seen).sort();
  }, [data]);

  if (!jobId) {
    return (
      <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
        Select a job from the Jobs tab to triage its DLQ entries.
      </div>
    );
  }

  const handleReplay = async (entry: MigrationDlqEntry) => {
    setActionError(null);
    try {
      await replayMutation.mutateAsync(entry);
    } catch (err) {
      setActionError(err && typeof err === 'object' && 'message' in err ? (err as Error).message : 'Replay failed');
    }
  };

  const handleSuppress = async (entry: MigrationDlqEntry) => {
    setActionError(null);
    try {
      await suppressMutation.mutateAsync(entry);
    } catch (err) {
      setActionError(err && typeof err === 'object' && 'message' in err ? (err as Error).message : 'Suppress failed');
    }
  };

  const handleBulkReplay = async () => {
    setActionError(null);
    setBulkResult(null);
    try {
      const result = await bulkMutation.mutateAsync({
        error_code: filters.error_code,
        error_class: filters.error_class,
      });
      setBulkResult(`Replayed ${result.replayed} entries: ${result.resolved} resolved, ${result.failed} still failing.`);
    } catch (err) {
      setActionError(err && typeof err === 'object' && 'message' in err ? (err as Error).message : 'Bulk replay failed');
    }
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 p-3 bg-gray-50 dark:bg-gray-800/40 rounded-lg">
        <FilterField label="Error code">
          <select
            value={filters.error_code ?? ''}
            onChange={(e) => setFilters({ ...filters, error_code: e.target.value || undefined })}
            className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm"
          >
            <option value="">Any</option>
            {distinctErrorCodes.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Error class">
          <select
            value={filters.error_class ?? ''}
            onChange={(e) => setFilters({ ...filters, error_class: e.target.value || undefined })}
            className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm"
          >
            <option value="">Any</option>
            <option value="transient">transient</option>
            <option value="per_message">per_message</option>
            <option value="per_folder">per_folder</option>
            <option value="per_job">per_job</option>
            <option value="tenant_boundary">tenant_boundary</option>
          </select>
        </FilterField>
        <FilterField label="Suppressed">
          <select
            value={filters.suppressed === undefined ? '' : filters.suppressed ? 'yes' : 'no'}
            onChange={(e) => {
              const v = e.target.value;
              setFilters({ ...filters, suppressed: v === '' ? undefined : v === 'yes' });
            }}
            className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm"
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
            <option value="">Both</option>
          </select>
        </FilterField>

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleBulkReplay}
            disabled={bulkMutation.isPending || !data || data.entries.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm bg-lex-accent text-white hover:bg-lex-accent-strong disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {bulkMutation.isPending ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <ArrowPathIcon className="h-4 w-4" />}
            Bulk replay (current filter)
          </button>
        </div>
      </div>

      {actionError && (
        <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/20 p-3 rounded">
          {actionError}
        </div>
      )}
      {bulkResult && (
        <div className="text-sm text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded">
          {bulkResult}
        </div>
      )}

      {/* List */}
      {isLoading && (
        <div className="text-center py-12 text-sm text-gray-500">
          <ArrowPathIcon className="h-5 w-5 mx-auto animate-spin" />
          <div className="mt-2">Loading DLQ entries…</div>
        </div>
      )}
      {isError && (
        <div className="text-center py-8 text-sm text-rose-700 dark:text-rose-300">
          Could not load DLQ entries.
        </div>
      )}
      {data && data.entries.length === 0 && (
        <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
          <ExclamationTriangleIcon className="h-6 w-6 mx-auto mb-2 text-gray-400" />
          No DLQ entries match this filter.
        </div>
      )}

      {data && data.entries.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/60">
              <tr>
                {['Error code', 'Class', 'Source locator', 'Attempts', 'Last seen', 'Actions'].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
              {data.entries.map((entry) => (
                <React.Fragment key={entry.dlq_id}>
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                    <td className="px-3 py-2 text-xs font-mono text-amber-700 dark:text-amber-400">{entry.error_code}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{entry.error_class}</td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-700 dark:text-gray-300 truncate max-w-xs">
                      {entry.source_locator}
                    </td>
                    <td className="px-3 py-2 text-xs tabular-nums text-gray-700 dark:text-gray-300">{entry.attempts}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {new Date(entry.last_seen_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <RowActionButton
                          icon={EyeIcon}
                          label={expandedId === entry.dlq_id ? 'Hide' : 'View'}
                          onClick={() => setExpandedId(expandedId === entry.dlq_id ? null : entry.dlq_id)}
                        />
                        <RowActionButton
                          icon={ArrowPathIcon}
                          label="Replay"
                          onClick={() => handleReplay(entry)}
                          disabled={entry.suppressed}
                          busy={replayMutation.isPending}
                        />
                        <RowActionButton
                          icon={StopIcon}
                          label="Suppress"
                          onClick={() => handleSuppress(entry)}
                          disabled={entry.suppressed}
                          busy={suppressMutation.isPending}
                          destructive
                        />
                      </div>
                    </td>
                  </tr>
                  {expandedId === entry.dlq_id && (
                    <tr className="bg-gray-50 dark:bg-gray-800/40">
                      <td colSpan={6} className="px-3 py-3">
                        <ExpandedDetail entry={entry} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <div className="bg-gray-50 dark:bg-gray-800/40 px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
            {data.total.toLocaleString()} total entries
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tiny atoms ──────────────────────────────────────────────────────────
function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      {children}
    </label>
  );
}

function RowActionButton({
  icon: Icon,
  label,
  onClick,
  busy,
  disabled,
  destructive,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  busy?: boolean;
  disabled?: boolean;
  destructive?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={busy || disabled}
      title={label}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
        destructive
          ? 'border border-rose-200 text-rose-700 dark:border-rose-800 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/20'
          : 'border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      <Icon className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
      <span>{label}</span>
    </button>
  );
}

function ExpandedDetail({ entry }: { entry: MigrationDlqEntry }) {
  return (
    <div className="space-y-2 text-xs">
      <div>
        <div className="font-semibold text-gray-700 dark:text-gray-300">Error message</div>
        <pre className="mt-1 p-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded font-mono whitespace-pre-wrap break-words text-gray-700 dark:text-gray-300">
          {entry.error_message}
        </pre>
      </div>
      {entry.diagnostic_context && Object.keys(entry.diagnostic_context).length > 0 && (
        <div>
          <div className="font-semibold text-gray-700 dark:text-gray-300">Diagnostic context</div>
          <pre className="mt-1 p-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded font-mono text-gray-700 dark:text-gray-300">
            {JSON.stringify(entry.diagnostic_context, null, 2)}
          </pre>
        </div>
      )}
      {entry.raw_blob_storage_path && (
        <div className="text-gray-500 dark:text-gray-400">
          Raw bytes stashed at: <span className="font-mono">{entry.raw_blob_storage_path}</span>
        </div>
      )}
      {entry.suppressed && (
        <div className="text-amber-700 dark:text-amber-300">
          Suppressed by {entry.suppressed_by_user_id} at {entry.suppressed_at && new Date(entry.suppressed_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}

export default MigrationDlqTriagePanel;
